import axios from 'axios';

// URL base - relativo para funcionar em qualquer dominio
const getBaseUrl = () => '';

// Função para aguardar
const _sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Quantas tentativas para erros transientes (gateway/Cloudflare)
// 🛡️ FIX 27-06-2026: estendido para 5 tentativas e inclui 520-524 (Cloudflare
// origin errors). Antes o retry só cobria 502/503/504 — o sintoma reportado
// pelo utilizador em produção era "HTTP 520 (após 3 tentativas)" precisamente
// porque o 520 não estava na lista. Backoff até ~12s cobre janelas de restart
// de container em prod.
const _MAX_GW_RETRIES = 5;
const _GW_RETRY_DELAY = 1500; // ms — incremento por tentativa (linear)
const _isTransientStatus = (status) =>
  status === 502 || status === 503 || status === 504 ||
  (status >= 520 && status <= 524);

const api = axios.create({
  timeout: 90000, // 90 segundos para operações normais
});

// Instância para operações longas (backup, exportação, etc.)
export const apiLongOperation = axios.create({
  timeout: 300000, // 5 minutos para operações longas
});

// Função para configurar interceptors em ambas instâncias
const setupInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const baseUrl = getBaseUrl();
    config.baseURL = baseUrl + '/api';
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.__retryCount = 0;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config || {};
      const status = error.response?.status;

      // Retry silencioso para gateway transientes (502/503/504) E Cloudflare 520-524
      // 🛡️ FIX 27-06-2026: incluir 520-524 (Cloudflare origin errors).
      const isGateway = _isTransientStatus(status);
      if ((isGateway || !status) && config.__retryCount < _MAX_GW_RETRIES) {
        config.__retryCount = (config.__retryCount || 0) + 1;
        await new Promise(r => setTimeout(r, _GW_RETRY_DELAY * config.__retryCount));
        return instance(config);
      }

      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }

      return Promise.reject(error);
    }
  );
};

// Configurar interceptors para apiLongOperation
setupInterceptors(apiLongOperation);

// Add token to requests AND set baseURL dynamically
api.interceptors.request.use((config) => {
  // SEMPRE definir baseURL dinamicamente para garantir URL correcta
  const baseUrl = getBaseUrl();
  config.baseURL = baseUrl + '/api';
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.__retryCount = config.__retryCount || 0;
  return config;
});

// Handle errors + RETRY automático para 502/503/504 (gateway)
// 🛡️ 18-05-2026: em produção (janeira.pt) com 1 worker uvicorn, o login
// dá ocasionalmente 502/504 quando o worker está bloqueado por outra
// request. Retry silencioso evita mostrar "Servidor indisponível" ao user.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};

    // Retry para gateway transientes (502/503/504) E Cloudflare 520-524
    const isGateway = _isTransientStatus(status);
    if ((isGateway || !status) && cfg && cfg.__retryCount < _MAX_GW_RETRIES) {
      cfg.__retryCount += 1;
      const delay = _GW_RETRY_DELAY * cfg.__retryCount;
      // eslint-disable-next-line no-console
      console.warn(`⚠️ HTTP ${status || 'network'} — retry ${cfg.__retryCount}/${_MAX_GW_RETRIES} em ${delay}ms`);
      await _sleep(delay);
      return api(cfg);
    }

    // Handle authentication errors
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getMe: () => api.get('/auth/me'),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const objetivosAPI = {
  getAll: (params) => api.get('/objetivos', { params }),
  list: (params) => api.get('/objetivos', { params }), // Alias para compatibilidade
  get: (path) => api.get(path), // Para endpoints customizados
  create: (data) => api.post('/objetivos', data),
  delete: (id) => api.delete(`/objetivos/${id}`),
};

export const regrasPontosAPI = {
  getAll: () => api.get('/regras-pontos'),
  list: () => api.get('/regras-pontos'), // Alias para compatibilidade
  create: (data) => api.post('/regras-pontos', data),
  update: (id, data) => api.put(`/regras-pontos/${id}`, data),
  delete: (id) => api.delete(`/regras-pontos/${id}`),
};

export const vendasAPI = {
  getAll: (params) => api.get('/vendas', { params }),
  list: (params) => api.get('/vendas', { params }), // Alias para compatibilidade
  create: (data) => api.post('/vendas', data),
  delete: (id) => api.delete(`/vendas/${id}`),
};

export const dashboardAPI = {
  get: (url) => api.get(url),
  getStats: (params) => api.get('/dashboard/stats', { params }),
  getRankingMensal: (params) => api.get('/ranking/mensal', { params }),
  getRankingAnual: (params) => api.get('/ranking/anual', { params }),
  getMeusPontos: () => api.get('/meus-pontos'),
  getContribuicoesXdMes: (ano, mes) => api.get(`/contribuicoes-xd/${ano}/${mes}`),
  getContribuicoesXdAno: (ano) => api.get(`/contribuicoes-xd/${ano}`),
  getPontosXdMes: (ano, mes) => api.get(`/pontos-xd/atingimento/${ano}/${mes}`),
  getPontosXdAno: (ano) => api.get(`/pontos-xd/atingimento/${ano}`),
};

export default api;
